import React, { useCallback, useEffect, useState, useRef, createContext, useContext } from 'react';
import { type ReactNode } from 'react';
const SCAN_COUNT_KEY = 'smart_feedback_scan_count';
const MILESTONES = [1, 5, 10, 20, 50, 100] as const;
type FeedbackMilestone = (typeof MILESTONES)[number] | null;
interface SmartFeedbackContextValue {
  isOpen: boolean;
  milestone: FeedbackMilestone;
  openLandingFeedback: () => void;
  closeFeedback: () => void;
}
const SmartFeedbackContext = createContext<SmartFeedbackContextValue>({
  isOpen: false,
  milestone: null,
  openLandingFeedback: () => {},
  closeFeedback: () => {}
});
interface SmartFeedbackProviderProps {
  children: ReactNode;
}
export function SmartFeedbackProvider({
  children
}: SmartFeedbackProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [milestone, setMilestone] = useState<FeedbackMilestone>(null);
  const promptTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearPendingPrompt = useCallback(() => {
    if (promptTimerRef.current) {
      clearTimeout(promptTimerRef.current);
      promptTimerRef.current = null;
    }
  }, []);
  const scheduleMilestonePrompt = useCallback((count: number) => {
    const reachedMilestone = MILESTONES.find(item => item === count);
    if (!reachedMilestone) return;
    const shownKey = `smart_feedback_shown_${reachedMilestone}`;
    if (localStorage.getItem(shownKey) === 'true') return;
    clearPendingPrompt();
    promptTimerRef.current = setTimeout(() => {
      setMilestone(reachedMilestone);
      setIsOpen(true);
      promptTimerRef.current = null;
    }, 1800);
  }, [clearPendingPrompt]);
  useEffect(() => {
    const handleScanCompleted = () => {
      const currentCount = Number.parseInt(localStorage.getItem(SCAN_COUNT_KEY) || '0', 10);
      const nextCount = currentCount + 1;
      localStorage.setItem(SCAN_COUNT_KEY, String(nextCount));
      scheduleMilestonePrompt(nextCount);
    };
    window.addEventListener('scanCompleted', handleScanCompleted);
    return () => {
      window.removeEventListener('scanCompleted', handleScanCompleted);
      clearPendingPrompt();
    };
  }, [clearPendingPrompt, scheduleMilestonePrompt]);
  const openLandingFeedback = useCallback(() => {
    clearPendingPrompt();
    setMilestone(null);
    setIsOpen(true);
  }, [clearPendingPrompt]);
  const closeFeedback = useCallback(() => {
    if (milestone) {
      localStorage.setItem(`smart_feedback_shown_${milestone}`, 'true');
    }
    clearPendingPrompt();
    setIsOpen(false);
    setMilestone(null);
  }, [clearPendingPrompt, milestone]);
  return <SmartFeedbackContext.Provider value={{
    isOpen,
    milestone,
    openLandingFeedback,
    closeFeedback
  }} data-id="element-3256">
      {children}
    </SmartFeedbackContext.Provider>;
}
export function useSmartFeedback() {
  return useContext(SmartFeedbackContext);
}